import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import { defineMessages, useIntl } from "react-intl";

import FormLayout from "@mapmycustomers/shared/types/layout/FormLayout";
import { Modal, TextField } from "@mapmycustomers/ui";

import { deleteLayout } from "@app/scene/settings/component/FormLayouts/store/actions";

import styles from "./ConfirmLayoutDeleteModal.module.scss";

interface Props {
  deleteLayout: typeof deleteLayout;
  id: FormLayout["id"];
  onHide: () => void;
}

const messages = defineMessages({
  confirm: {
    id: "settings.formLayouts.deleteLayout.modal.confirm",
    defaultMessage: "Delete Layout",
    description: "Delete Layout button title",
  },
  delete: {
    id: "settings.formLayouts.deleteLayout.modal.delete",
    defaultMessage: "To delete, type <b>DELETE</b> to confirm",
    description: "Delete layout modal confirmation text",
  },
  error: {
    id: "settings.formLayouts.deleteLayout.modal.deleteTextError",
    defaultMessage: "Text must match exactly",
    description: "Delete text error for Delete Layout modal",
  },
  text: {
    id: "settings.formLayouts.deleteLayout.modal.text",
    defaultMessage:
      "This action will permanently delete this layout. If this layout is assigned to any users, another layout will be applied instead. Deletion cannot be reversed.",
    description: "Delete Layout confirmation text",
  },
  title: {
    id: "settings.formLayouts.deleteLayout.modal.title",
    defaultMessage: "Confirm layout deletion",
    description: "Delete Layout modal title",
  },
});

const ConfirmLayoutDeleteModal: React.FC<Props> = ({ id, deleteLayout, onHide }) => {
  const intl = useIntl();

  const [deleteText, setDeleteText] = useState<string>("");
  const [deleteTextError, setDeleteTextError] = useState<string | undefined>();

  const handleConfirm = useCallback(() => {
    if (deleteText.trim().toLowerCase() !== "delete") {
      setDeleteTextError(intl.formatMessage(messages.error));
    } else {
      setDeleteTextError(undefined);
      deleteLayout(id);
      onHide();
    }
  }, [deleteLayout, deleteText, id, intl, onHide, setDeleteTextError]);

  return (
    <Modal
      centered
      okButtonProps={{ danger: true, type: "default" }}
      okText={intl.formatMessage(messages.confirm)}
      onCancel={onHide}
      onOk={handleConfirm}
      open
      title={intl.formatMessage(messages.title)}
    >
      <div className={styles.container}>
        <div className={styles.text}>{intl.formatMessage(messages.text)}</div>
        <div className={styles.input}>
          <TextField
            className={styles.nameField}
            error={deleteTextError}
            label={intl.formatMessage(messages.delete, { b: (text) => <b>{text}</b> })}
            onChange={setDeleteText}
            value={deleteText}
          />
        </div>
      </div>
    </Modal>
  );
};

const mapDispatchToProps = {
  deleteLayout,
};

export default connect(null, mapDispatchToProps)(ConfirmLayoutDeleteModal);
